import cronParser from "cron-parser";
import cronstrue from "cronstrue";
import { format, isBefore, parseISO } from "date-fns";
import useApi from "../hooks/useGaneshApi";
import { SchedulingEvent_T } from "../types/Scheduling";

export const translate_cron = async (
  cron_expression: string
): Promise<string> => {
  try {
    cronParser.parseExpression(cron_expression);
  } catch (e) {
    throw new Error(`Unable to parse cron expression ${cron_expression}`);
  }

  const human_expression = cronstrue.toString(cron_expression);
  return human_expression;
};

export const generate_schedule = async (
  date_start: string,
  date_end: string,
  cron_expression: string,
  date_off_sched_first_delivery: string,
  upgrade_cadence: number
): Promise<SchedulingEvent_T[]> => {
  const events: SchedulingEvent_T[] = [];

  const startDateISO = new Date(date_start).toISOString();
  const endDateISO = new Date(date_end).toISOString();

  const interval = cronParser.parseExpression(cron_expression, {
    currentDate: startDateISO,
    endDate: endDateISO,
    iterator: true,
  });

  let count = 1;
  try {
    if (date_off_sched_first_delivery) {
      const event: SchedulingEvent_T = {
        target_fulfillment_date: format(
          date_off_sched_first_delivery,
          "yyyy-MM-dd"
        ),
        target_fulfillment_dow: format(date_off_sched_first_delivery, "EEEE"),
        schedule_volume_upgrade: false,
      };
      events.push(event);
      count++;
    }

    let nextDateObj = interval.next();

    while (true && !nextDateObj.done) {
      let nextDate = nextDateObj.value.toDate();

      if (isBefore(parseISO(date_end), nextDate)) break;

      const scheduleVolumeUpgrade = count % upgrade_cadence === 0 && count != 1;

      const event: SchedulingEvent_T = {
        target_fulfillment_date: format(nextDate, "yyyy-MM-dd"),
        target_fulfillment_dow: format(nextDate, "EEEE"),
        schedule_volume_upgrade: scheduleVolumeUpgrade,
      };

      events.push(event);
      count++;
      nextDateObj = interval.next();
    }
  } catch (e) {
    throw new Error(`Unable to generate schedule. ${e}`);
  }

  return events;
};

export const useCommitSchedule = () => {
  const { apiRequest } = useApi();

  const removeAndAddSchedule = async (
    overwrite_delivery_ids: number[],
    date_start: string,
    date_end: string,
    new_schedule: SchedulingEvent_T[],
    admin_id: string,
    onStatusUpdate: (message: string) => void
  ) => {
    try {
      if (overwrite_delivery_ids.length > 0) {
        let remove_payload = {
          delivery_ids: overwrite_delivery_ids,
          dateStart: date_start,
          dateEnd: date_end,
          admin_id: admin_id,
        };
        // First perform overwrite, if any
        onStatusUpdate(
          `Removing existing schedule for ${overwrite_delivery_ids.length} delivery_ids...`
        );
        const removeResponse = await apiRequest(
          `/schedule/batch-remove-deliveries-date-range`,
          {
            method: "DELETE",
            body: remove_payload,
          }
        );

        if (removeResponse.error) {
          onStatusUpdate(
            "Failed to remove existing schedule: " +
              JSON.stringify(removeResponse.error)
          );
          return { success: false };
        }

        // Notify that removal is done
        onStatusUpdate("Removal complete. Preparing to add new schedule.");
      }

      onStatusUpdate("Building the new schedule to add.");
      // Create a new array that contains the schedule merged with delivery IDs
      const mergedSchedule = overwrite_delivery_ids.flatMap((delivery_id) =>
        new_schedule.map((schedule) => ({
          ...schedule,
          delivery_id: delivery_id,
        }))
      );

      let add_payload = {
        deliveries: mergedSchedule,
        admin_id: admin_id,
      };

      onStatusUpdate("Adding new schedule.");
      // Call the add schedule endpoint
      const addResponse = await apiRequest("/schedule/batch-add-deliveries", {
        method: "POST",
        body: add_payload,
      });

      if (addResponse.error) {
        onStatusUpdate(
          "Failed to add the schedule: " + JSON.stringify(addResponse.error)
        );
        return { success: false };
      }

      // Notify user that the schedule has been added successfully
      onStatusUpdate("Schedule successfully updated.");
      return { success: true };
    } catch (error) {
      onStatusUpdate(`Error: ${(error as Error).message}`);
      return { success: false };
    }
  };

  return {
    removeAndAddSchedule,
  };
};
